import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  notes?: string;
  businessOwnerId: string;
  createdAt: string;
  lastAppointment?: string;
  totalAppointments?: number;
  totalSpent?: number;
  tags?: string[];
}

const STORAGE_KEY = 'customers';

class CustomerService {
  // Get all customers for a business owner
  async getCustomers(businessOwnerId: string): Promise<Customer[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedCustomers: Customer[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            const customersDoc = await getDoc(doc(db, 'businesses', businessOwnerId, 'customers', 'all'));
            if (customersDoc.exists()) {
              const data = customersDoc.data();
              loadedCustomers = data.customers || [];
              loadedFromFirestore = true;
              console.log(`✅ CustomerService: Loaded ${loadedCustomers.length} customers from Firestore`);
              
              // Sync to local storage for offline access
              // Also merge with existing local storage to preserve data
              const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
              const localCustomers = allCustomers ? JSON.parse(allCustomers) : [];
              
              // Merge Firestore data with local (Firestore takes precedence)
              const mergedCustomers = [...localCustomers];
              loadedCustomers.forEach((firestoreCustomer: Customer) => {
                const localIndex = mergedCustomers.findIndex((c: Customer) => c.id === firestoreCustomer.id);
                if (localIndex >= 0) {
                  mergedCustomers[localIndex] = firestoreCustomer;
                } else {
                  mergedCustomers.push(firestoreCustomer);
                }
              });
              
              await SimpleStorage.setItem(STORAGE_KEY, JSON.stringify(mergedCustomers));
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  CustomerService: Could not load customers from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
        const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
        if (allCustomers) {
          const customers = JSON.parse(allCustomers);
          loadedCustomers = customers.filter((c: Customer) => c.businessOwnerId === businessOwnerId);
          console.log(`📋 CustomerService: Loaded ${loadedCustomers.length} customers from local storage`);
        }
      } else {
        // Filter by business owner
        loadedCustomers = loadedCustomers.filter((c: Customer) => c.businessOwnerId === businessOwnerId);
      }
      
      return loadedCustomers;
    } catch (error) {
      console.error('Error getting customers:', error);
      return [];
    }
  }

  // Get a single customer by ID
  async getCustomerById(customerId: string): Promise<Customer | null> {
    try {
      const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
      if (!allCustomers) {
        return null;
      }
      
      const customers = JSON.parse(allCustomers);
      return customers.find((c: Customer) => c.id === customerId) || null;
    } catch (error) {
      console.error('Error getting customer:', error);
      return null;
    }
  }

  // Add a new customer
  async addCustomer(customer: Omit<Customer, 'id' | 'createdAt'>): Promise<Customer> {
    try {
      const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
      const customers = allCustomers ? JSON.parse(allCustomers) : [];
      
      const newCustomer: Customer = {
        ...customer,
        id: `customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        totalAppointments: 0,
        totalSpent: 0,
      };
      
      customers.push(newCustomer);
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(STORAGE_KEY, JSON.stringify(customers));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { doc, getDoc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            // Get existing customers for this business owner
            const customersDoc = await getDoc(doc(db, 'businesses', customer.businessOwnerId, 'customers', 'all'));
            const existingCustomers = customersDoc.exists() ? customersDoc.data().customers || [] : [];
            
            // Add new customer
            const updatedCustomers = [...existingCustomers, newCustomer];
            
            await setDoc(doc(db, 'businesses', customer.businessOwnerId, 'customers', 'all'), {
              customers: updatedCustomers,
              updatedAt: new Date().toISOString(),
            });
            console.log('✅ CustomerService: Customer saved to Firestore');
          }
        } catch (firestoreError: any) {
          console.error('❌ CustomerService: Error saving customer to Firestore:', firestoreError);
          console.warn('⚠️  Customer saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
      
      console.log('✅ Customer added:', newCustomer.name);
      return newCustomer;
    } catch (error) {
      console.error('Error adding customer:', error);
      throw error;
    }
  }

  // Update an existing customer
  async updateCustomer(customerId: string, updates: Partial<Customer>): Promise<boolean> {
    try {
      const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
      if (!allCustomers) {
        return false;
      }
      
      const customers = JSON.parse(allCustomers);
      const index = customers.findIndex((c: Customer) => c.id === customerId);
      
      if (index === -1) {
        return false;
      }
      
      customers[index] = { ...customers[index], ...updates };
      const updatedCustomer = customers[index];
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(STORAGE_KEY, JSON.stringify(customers));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web' && updatedCustomer.businessOwnerId) {
        try {
          const { doc, getDoc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            const customersDoc = await getDoc(doc(db, 'businesses', updatedCustomer.businessOwnerId, 'customers', 'all'));
            const existingCustomers = customersDoc.exists() ? customersDoc.data().customers || [] : [];
            
            // Update customer in array
            const updatedCustomers = existingCustomers.map((c: Customer) => 
              c.id === customerId ? { ...c, ...updates } : c
            );
            
            await setDoc(doc(db, 'businesses', updatedCustomer.businessOwnerId, 'customers', 'all'), {
              customers: updatedCustomers,
              updatedAt: new Date().toISOString(),
            });
            console.log('✅ CustomerService: Customer updated in Firestore');
          }
        } catch (firestoreError: any) {
          console.error('❌ CustomerService: Error updating customer in Firestore:', firestoreError);
          // Don't throw - local update succeeded
        }
      }
      
      console.log('✅ Customer updated:', customerId);
      return true;
    } catch (error) {
      console.error('Error updating customer:', error);
      return false;
    }
  }

  // Delete a customer
  async deleteCustomer(customerId: string): Promise<boolean> {
    try {
      const allCustomers = await SimpleStorage.getItem(STORAGE_KEY);
      if (!allCustomers) {
        return false;
      }
      
      const customers = JSON.parse(allCustomers);
      const customerToDelete = customers.find((c: Customer) => c.id === customerId);
      const filteredCustomers = customers.filter((c: Customer) => c.id !== customerId);
      
      if (filteredCustomers.length === customers.length) {
        return false; // Customer not found
      }
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(STORAGE_KEY, JSON.stringify(filteredCustomers));
      
      // Also delete from Firestore (for cross-browser/device access)
      if (Platform.OS === 'web' && customerToDelete?.businessOwnerId) {
        try {
          const { doc, getDoc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            const customersDoc = await getDoc(doc(db, 'businesses', customerToDelete.businessOwnerId, 'customers', 'all'));
            const existingCustomers = customersDoc.exists() ? customersDoc.data().customers || [] : [];
            
            // Remove customer from array
            const updatedCustomers = existingCustomers.filter((c: Customer) => c.id !== customerId);
            
            await setDoc(doc(db, 'businesses', customerToDelete.businessOwnerId, 'customers', 'all'), {
              customers: updatedCustomers,
              updatedAt: new Date().toISOString(),
            });
            console.log('✅ CustomerService: Customer deleted from Firestore');
          }
        } catch (firestoreError: any) {
          console.error('❌ CustomerService: Error deleting customer from Firestore:', firestoreError);
          // Don't throw - local delete succeeded
        }
      }
      
      console.log('✅ Customer deleted:', customerId);
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return false;
    }
  }

  // Search customers by name, email, or phone
  async searchCustomers(businessOwnerId: string, searchTerm: string): Promise<Customer[]> {
    try {
      const customers = await this.getCustomers(businessOwnerId);
      const lowerSearch = searchTerm.toLowerCase();
      
      return customers.filter(customer =>
        customer.name.toLowerCase().includes(lowerSearch) ||
        customer.email.toLowerCase().includes(lowerSearch) ||
        customer.phone.includes(searchTerm)
      );
    } catch (error) {
      console.error('Error searching customers:', error);
      return [];
    }
  }

  // Import customers from array (bulk import)
  async importCustomers(customers: Omit<Customer, 'id' | 'createdAt'>[]): Promise<number> {
    try {
      let importedCount = 0;
      
      for (const customer of customers) {
        await this.addCustomer(customer);
        importedCount++;
      }
      
      console.log(`✅ Imported ${importedCount} customers`);
      return importedCount;
    } catch (error) {
      console.error('Error importing customers:', error);
      throw error;
    }
  }

  // Get customer statistics
  async getCustomerStats(businessOwnerId: string): Promise<{
    totalCustomers: number;
    newThisMonth: number;
    activeCustomers: number;
  }> {
    try {
      const customers = await this.getCustomers(businessOwnerId);
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const newThisMonth = customers.filter(c => 
        new Date(c.createdAt) >= firstDayOfMonth
      ).length;
      
      const activeCustomers = customers.filter(c => 
        c.totalAppointments && c.totalAppointments > 0
      ).length;
      
      return {
        totalCustomers: customers.length,
        newThisMonth,
        activeCustomers,
      };
    } catch (error) {
      console.error('Error getting customer stats:', error);
      return {
        totalCustomers: 0,
        newThisMonth: 0,
        activeCustomers: 0,
      };
    }
  }

  // Update customer appointment stats
  async updateCustomerAppointmentStats(
    customerId: string,
    appointmentAmount: number
  ): Promise<boolean> {
    try {
      const customer = await this.getCustomerById(customerId);
      if (!customer) {
        return false;
      }
      
      const totalAppointments = (customer.totalAppointments || 0) + 1;
      const totalSpent = (customer.totalSpent || 0) + appointmentAmount;
      const lastAppointment = new Date().toISOString();
      
      return await this.updateCustomer(customerId, {
        totalAppointments,
        totalSpent,
        lastAppointment,
      });
    } catch (error) {
      console.error('Error updating customer stats:', error);
      return false;
    }
  }
}

export const customerService = new CustomerService();
export default customerService;



